import { userObsoleteApi, usersManagementApi } from 'app/services/api';
import { normalize, schema } from 'normalizr';

import { RoleName } from 'constants/roleNames';
import { catchAsyncActionError } from 'utils/action';
import { getConditionalObject } from 'utils/data';
import { createNamedActions } from 'utils/namedActionTypesProxy';

import { selectIsMasterCompany, selectMasterCompanyId } from 'store/auth/selectors';

export const USERS_ACTION_TYPE = createNamedActions('users', {
  GET_USERS: 'GET_USERS',
  GET_USER: 'GET_USER',
  CREATE_USER: 'CREATE_USER',
  EDIT_USER: 'EDIT_USER',
  DELETE_USER: 'DELETE_USER',
  RESET_USERS_STATE: 'RESET_USERS_STATE',
  SET_USER_PROPERTY: 'SET_USER_PROPERTY',
});

const user = new schema.Entity('data', {}, { idAttribute: 'id' });
const ADMIN_PANEL_OBSOLETE_USER_TYPE = 0; // TODO: remove it after BE changes.

export const getUsersAction = catchAsyncActionError(
  ({
    companyId: currentCompanyId,
    includeDeleted,
    rn: paramsRn,
    allRoles = false,
    es = true,
    scid,
    ...params
  } = {}) =>
    async (dispatch, getState) => {
      const state = getState();
      const masterCompanyId = selectMasterCompanyId(state);
      const companyId = currentCompanyId ?? masterCompanyId;

      const { data } = await usersManagementApi.getUsers({
        ...params,
        companyId,
        isDeleted: includeDeleted ? '' : false,
        ...getConditionalObject(!allRoles, { rn: paramsRn || RoleName.EMPLOYEE }),
        es,
        scid,
      });

      const mergedData = data.map(user => ({ ...user, companyId }));

      return dispatch({
        type: USERS_ACTION_TYPE.GET_USERS,
        payload: normalize(mergedData, [user]).entities.data,
      });
    },
);

export const getUserAction = catchAsyncActionError(
  ({ companyId, ...params } = {}) =>
    async dispatch => {
      const { data } = await usersManagementApi.getUser({
        ...params,
        companyId,
        rn: RoleName.EMPLOYEE,
      });

      return dispatch({
        type: USERS_ACTION_TYPE.GET_USER,
        payload: { ...data, companyId },
      });
    },
);

export const createUserAction = catchAsyncActionError(
  ({ companyId, ...params }) =>
    async dispatch => {
      const { data } = await usersManagementApi.createUser({
        ...params,
        companyId,
        userType: ADMIN_PANEL_OBSOLETE_USER_TYPE,
        roleName: RoleName.EMPLOYEE,
      });

      return dispatch({
        type: USERS_ACTION_TYPE.CREATE_USER,
        payload: { ...data, companyId },
      });
    },
);

export const editUserAction = catchAsyncActionError(
  ({ companyId, ...params }) =>
    async dispatch => {
      const { data } = await usersManagementApi.editUser({ ...params, companyId });

      return dispatch({
        type: USERS_ACTION_TYPE.EDIT_USER,
        payload: { ...data, companyId },
      });
    },
);

export const deleteUserAction = catchAsyncActionError(
  ({ companyId, ...params }) =>
    async dispatch => {
      await usersManagementApi.deleteUser({ ...params, companyId });

      return dispatch({
        type: USERS_ACTION_TYPE.DELETE_USER,
        meta: params,
      });
    },
);

export const resetPasswordAction = catchAsyncActionError(params => async (__, getState) => {
  const isMasterCompany = selectIsMasterCompany(params.companyId)(getState());
  const apiCall = isMasterCompany
    ? userObsoleteApi.resetPassword
    : userObsoleteApi.resetInternalPassword;

  const { data } = await apiCall(params);

  return data;
});

export const generateAppSecretAction = catchAsyncActionError(
  ({ companyId, userId }) =>
    async dispatch => {
      const { data } = await usersManagementApi.generateAppSecret({ companyId, userId });

      if (!data) {
        throw new Error('Failed to generate app secret: No data received');
      }

      const userData = { ...data, companyId };
      const { id } = userData;

      dispatch({
        type: USERS_ACTION_TYPE.GET_USER,
        payload: { result: id, entities: { data: { [id]: userData } } },
      });

      return userData;
    },
);

export const setUserIdentityFlagAction = catchAsyncActionError(
  ({ userId, propertyName, value }) =>
    async dispatch => {
      const stringValue = String(value);

      await usersManagementApi.updateUserProperties({
        userId,
        properties: [{ name: propertyName, value: stringValue }],
      });

      return dispatch({
        type: USERS_ACTION_TYPE.SET_USER_PROPERTY,
        meta: { userId },
        payload: { name: propertyName, value: stringValue },
      });
    },
);

export const setUserIdentityFlagsAction = catchAsyncActionError(
  ({ userId, flags }) =>
    async dispatch => {
      const properties = flags.map(({ propertyName, value }) => ({
        name: propertyName,
        value: String(value),
      }));

      await usersManagementApi.updateUserProperties({ userId, properties });

      properties.forEach(property =>
        dispatch({
          type: USERS_ACTION_TYPE.SET_USER_PROPERTY,
          meta: { userId },
          payload: property,
        }),
      );
    },
);

export const resetUsersStateAction = () => ({ type: USERS_ACTION_TYPE.RESET_USERS_STATE });
